import {
  faPlus,
  faPencil,
  faCheckCircle,
  faCalendarDay,
  faCalculator,
  faFileInvoiceDollar,
  faTrashCan,
  faPaperPlane,
  faArrowsRotate,
  faMoneyCheckDollar,
  faShieldHalved,
  faTowerBroadcast,
  faBinoculars,
  IconDefinition
} from '@fortawesome/free-solid-svg-icons';

import {
  InsurancePolicyTimelineEvent,
  TimelinePolicy
} from '@/repositories/InsurancePolicyTimelineRepository';
import type { PillProps } from '@/ui';
import { formatNumericCalendarDate } from '@/utils/dates';

export interface EventBadge {
  text: string;
  style: string;
}

export interface EventStatus {
  text: string;
  color: NonNullable<PillProps['color']>;
}

export interface EventPalette {
  dot: string;
  edge: string;
}

const PALETTES = {
  blue: { dot: 'bg-blue-500', edge: 'border-l-blue-500' },
  green: { dot: 'bg-green-500', edge: 'border-l-green-500' },
  red: { dot: 'bg-red-500', edge: 'border-l-red-500' },
  yellow: { dot: 'bg-yellow-500', edge: 'border-l-yellow-500' },
  purple: { dot: 'bg-purple-500', edge: 'border-l-purple-500' },
  indigo: { dot: 'bg-indigo-500', edge: 'border-l-indigo-500' },
  teal: { dot: 'bg-teal-500', edge: 'border-l-teal-500' },
  cyan: { dot: 'bg-cyan-500', edge: 'border-l-cyan-500' },
  amber: { dot: 'bg-amber-500', edge: 'border-l-amber-500' },
  emerald: { dot: 'bg-emerald-500', edge: 'border-l-emerald-500' },
  sky: { dot: 'bg-sky-500', edge: 'border-l-sky-500' },
  gray: { dot: 'bg-gray-500', edge: 'border-l-gray-400' }
} satisfies Record<string, EventPalette>;

type PaletteName = keyof typeof PALETTES;

export interface EventDisplay {
  label: string;
  status?: EventStatus;
  palette: EventPalette;
  icon: IconDefinition;
  iconColor: string;
  badges: EventBadge[];
}

const EVENT_LABELS: Record<string, string> = {
  policy_created: 'Policy Created',
  plan_start: 'Plan Start Date',
  plan_end: 'Plan End Date',
  policy_field_change: 'Policy Updated',
  insurance_verification: 'VOB',
  external_policy_verification: 'External Verification',
  billing_item: 'Billing Item',
  case_rate_period: 'Case Rate Period',
  claim_submitted: 'Claim Submitted',
  claim_status_change: 'Claim Status Change',
  remit_received: 'Remit Received'
};

const EVENT_PALETTES: Record<string, PaletteName> = {
  policy_created: 'blue',
  plan_start: 'green',
  plan_end: 'red',
  policy_field_change: 'blue',
  insurance_verification: 'purple',
  external_policy_verification: 'purple',
  billing_item: 'indigo',
  case_rate_period: 'teal',
  claim_submitted: 'cyan',
  claim_status_change: 'amber',
  remit_received: 'emerald'
};

const EVENT_ICONS: Record<string, IconDefinition> = {
  policy_created: faPlus,
  plan_start: faCalendarDay,
  plan_end: faCalendarDay,
  policy_field_change: faPencil,
  insurance_verification: faShieldHalved,
  external_policy_verification: faTowerBroadcast,
  billing_item: faFileInvoiceDollar,
  case_rate_period: faCalculator,
  claim_submitted: faPaperPlane,
  claim_status_change: faArrowsRotate,
  remit_received: faMoneyCheckDollar
};

interface VobStatusDisplay {
  suffix: string;
  palette: PaletteName;
  pillColor: EventStatus['color'];
}

const VOB_STATUS_MAP: Record<string, VobStatusDisplay> = {
  verified: { suffix: 'Verified', palette: 'green', pillColor: 'primary' },
  'billing requirements met': {
    suffix: 'Billing Requirements Met',
    palette: 'green',
    pillColor: 'primary'
  },
  'failed verification': {
    suffix: 'Failed Verification',
    palette: 'red',
    pillColor: 'destructive'
  },
  'pending verification': {
    suffix: 'Pending Verification',
    palette: 'yellow',
    pillColor: 'warning'
  },
  'pending coordination of benefits': {
    suffix: 'Pending COB',
    palette: 'sky',
    pillColor: 'secondary'
  }
};

function resolveVobStatus(status: string | null | undefined): VobStatusDisplay | null {
  if (!status) return null;
  return VOB_STATUS_MAP[status.toLowerCase().replace(/_/g, ' ')] ?? null;
}

export function cleanPolicyValue(value: string | null | undefined): string | null {
  if (!value || value === 'None' || value === 'NOT_SET') return null;
  return value;
}

export function truncateText(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max).trimEnd()}...` : value;
}

export function policyDisplayName(policy: TimelinePolicy): string {
  return (
    cleanPolicyValue(policy.insurance_provider_name) ||
    cleanPolicyValue(policy.carrier) ||
    'Unnamed policy'
  );
}

// Plan dates read exactly as the member profile's insurance section renders them.
export const PLAN_DATE_FIELDS = new Set(['plan_start_date', 'plan_end_date']);

// Important fields (all are...hue) but will key color changes
// for clarity
const COVERAGE_FIELDS = new Set([
  'verification',
  'plan_start_date',
  'plan_end_date',
  'priority_type'
]);

export function formatPlanDate(value: unknown): string {
  const text = String(value);

  return /^\d{4}-\d{2}-\d{2}/.test(text) ? formatNumericCalendarDate(text) : text;
}

export function formatCoverageRange(policy: TimelinePolicy): string {
  const start = policy.plan_start_date ? formatPlanDate(policy.plan_start_date) : null;
  const end = policy.plan_end_date ? formatPlanDate(policy.plan_end_date) : null;

  if (!start && !end) return '';
  return `${start || 'unknown'} - ${end || 'present'}`;
}

export function maskIdentifier(value: string | null | undefined): string {
  const clean = cleanPolicyValue(value);
  return clean ? `••${clean.slice(-4)}` : '';
}

export function shortPolicyId(policy: TimelinePolicy): string {
  return policy.id.slice(0, 8);
}

// Ordered from most to least useful for telling two policies apart.
function policyLabelParts(policy: TimelinePolicy): string[] {
  return [
    policyDisplayName(policy),
    formatCoverageRange(policy),
    maskIdentifier(policy.policy_member_id || policy.subscriber_id),
    cleanPolicyValue(policy.plan) || '',
    cleanPolicyValue(policy.priority_type) || ''
  ];
}

function joinParts(parts: string[], depth: number): string {
  return parts.slice(0, depth).filter(Boolean).join(' · ');
}

export function formatPolicyLabel(policy: TimelinePolicy, roster: TimelinePolicy[]): string {
  const parts = policyLabelParts(policy);
  const others = roster.filter((other) => other.id !== policy.id).map(policyLabelParts);

  const suffix = `${policy.deleted_at ? ' (deleted)' : ''} (${shortPolicyId(policy)})`;

  for (let depth = 1; depth <= parts.length; depth += 1) {
    const candidate = joinParts(parts, depth);
    const collides = others.some((otherParts) => joinParts(otherParts, depth) === candidate);
    if (!collides) return `${candidate}${suffix}`;
  }

  return `${joinParts(parts, parts.length)}${suffix}`;
}

// Events can belong to a group that is not a policy at all.
export const SENTINEL_POLICY_LABELS: Record<string, string> = {
  no_policy: 'No policy / self-pay',
  unknown: 'Unlinked verifications'
};

// The rail already names the policy, so a card only earns a carrier or priority chip
// when what the event recorded differs from what the policy says today.
export function derivePointInTimeDrift(
  event: InsurancePolicyTimelineEvent,
  policy?: TimelinePolicy
): { carrier: string | null; priority: string | null } {
  const eventCarrier =
    cleanPolicyValue(event.data.carrier) || cleanPolicyValue(event.data.insurance_provider_name);
  const eventPriority = cleanPolicyValue(event.data.priority_type);

  if (!policy) {
    return { carrier: eventCarrier, priority: eventPriority };
  }

  const currentCarrier =
    cleanPolicyValue(policy.insurance_provider_name) || cleanPolicyValue(policy.carrier);
  const currentPriority = cleanPolicyValue(policy.priority_type);

  return {
    carrier: eventCarrier && eventCarrier !== currentCarrier ? eventCarrier : null,
    priority:
      eventPriority && eventPriority.toLowerCase() !== (currentPriority || '').toLowerCase()
        ? eventPriority
        : null
  };
}

// One external verification fans out into a coverage per policy it matched, each
// repeating the parent's payload — so a discovery run arrives as several near
// identical events stamped with the same second.  They collapse into one row.
export function groupCoverages(
  events: InsurancePolicyTimelineEvent[]
): InsurancePolicyTimelineEvent[][] {
  const groups: InsurancePolicyTimelineEvent[][] = [];

  events.forEach((event) => {
    const current = groups[groups.length - 1];
    const evId = event.data.ev_id;

    const belongsToCurrent =
      current &&
      evId &&
      event.event_type === 'external_policy_verification' &&
      current[0].event_type === 'external_policy_verification' &&
      current[0].data.ev_id === evId;

    if (belongsToCurrent) current.push(event);
    else groups.push([event]);
  });

  return groups;
}

export function isDeletionEvent(event: InsurancePolicyTimelineEvent): boolean {
  return (
    event.event_type === 'policy_field_change' &&
    event.data.changes &&
    'deleted_at' in event.data.changes
  );
}

export function getVobStatusStyle(status: string): string {
  const s = status.toLowerCase().replace(/_/g, ' ');
  if (s === 'verified' || s.includes('billing requirements met'))
    return 'bg-green-100 text-green-800';
  if (s.includes('fail')) return 'bg-red-100 text-red-800';
  if (s.includes('pending')) return 'bg-yellow-100 text-yellow-800';
  return 'bg-gray-100 text-gray-800';
}

export function getServiceClassStyle(serviceClass: string): string {
  const s = serviceClass.toLowerCase().replace(/_/g, ' ');
  if (s.includes('mental') || s.includes('behavioral')) return 'bg-violet-100 text-violet-800';
  if (s.includes('substance') || s.includes('sud') || s.includes('detox'))
    return 'bg-orange-100 text-orange-800';
  if (s.includes('medical') || s.includes('med')) return 'bg-sky-100 text-sky-800';
  return 'bg-gray-100 text-gray-800';
}

export function deriveEventDisplay(event: InsurancePolicyTimelineEvent): EventDisplay {
  const baseLabel = EVENT_LABELS[event.event_type] || event.event_type;
  const basePalette = PALETTES[EVENT_PALETTES[event.event_type] ?? 'gray'];
  const baseIcon = EVENT_ICONS[event.event_type] || faCheckCircle;

  switch (event.event_type) {
    case 'insurance_verification': {
      const status = event.data.status || event.data.verification_status;
      const vobDisplay = resolveVobStatus(status);
      return {
        label: 'VOB',
        status: status
          ? { text: vobDisplay?.suffix ?? status, color: vobDisplay?.pillColor ?? 'neutral' }
          : undefined,
        palette: vobDisplay ? PALETTES[vobDisplay.palette] : basePalette,
        icon: baseIcon,
        iconColor: 'text-white',
        badges: []
      };
    }

    case 'external_policy_verification': {
      let label = baseLabel;
      if (event.policy_id === 'unknown') {
        label = 'External Verification - Unlinked to Insurance Policy';
      } else if (event.data.is_discovery) {
        label = 'Discovery Verification';
      }
      const badges: EventBadge[] = [];
      if (event.data.eligibility_status_description) {
        badges.push({
          text: event.data.eligibility_status_description,
          style: 'bg-gray-200 text-gray-800'
        });
      }
      return {
        label,
        palette: basePalette,
        icon: event.data.is_discovery ? faBinoculars : baseIcon,
        iconColor: 'text-white',
        badges
      };
    }

    case 'policy_field_change': {
      const changes = event.data.changes || {};
      const changesCoverage = Object.keys(changes).some((field) => COVERAGE_FIELDS.has(field));

      if ('deleted_at' in changes) {
        return {
          label: 'Policy Deleted',
          palette: PALETTES.red,
          icon: faTrashCan,
          iconColor: 'text-white',
          badges: []
        };
      }

      const badges: EventBadge[] = [];
      if ('priority_type' in changes) {
        badges.push({ text: 'Priority Changed', style: 'bg-indigo-100 text-indigo-800' });
      }
      return {
        label: baseLabel,
        palette: changesCoverage ? PALETTES.amber : basePalette,
        icon: baseIcon,
        iconColor: 'text-white',
        badges
      };
    }

    case 'billing_item': {
      const badges: EventBadge[] = [];
      if (event.data.service_type) {
        badges.push({ text: event.data.service_type, style: 'bg-indigo-100 text-indigo-800' });
      }
      if (event.data.service_class) {
        badges.push({
          text: event.data.service_class,
          style: getServiceClassStyle(event.data.service_class)
        });
      }
      return {
        label: baseLabel,
        palette: basePalette,
        icon: baseIcon,
        iconColor: 'text-white',
        badges
      };
    }

    default:
      return {
        label: baseLabel,
        palette: basePalette,
        icon: baseIcon,
        iconColor: 'text-white',
        badges: []
      };
  }
}

export function deriveDisplayCarrier(event: InsurancePolicyTimelineEvent): string {
  if (event.event_type === 'external_policy_verification' && event.policy_id === 'unknown') {
    return 'Unlinked to Insurance Policy';
  }
  return (
    cleanPolicyValue(event.data.carrier) ||
    cleanPolicyValue(event.data.insurance_provider_name) ||
    'Carrier not provided'
  );
}

export function deriveDisplayPriority(event: InsurancePolicyTimelineEvent): string | null {
  return event.data.priority_type || null;
}
